'use client';

/* eslint-disable tailwindcss/no-arbitrary-value -- the 30px scroll button has no token or scale value */
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useCmsClientContext } from '../../../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../../../utils/translations/tr';
import { MaterialIcon } from '../../../../../MaterialIcon';
import { TRANSLATIONS } from '../../i18n';

export type ProductDetailsSectionOffersPlTableCell =
  | { kind: 'value'; value: string }
  | { kind: 'seeDetails' }
  | { kind: 'empty' };

export type ProductDetailsSectionOffersPlTableProps = {
  rowHeaderLabel: string;
  rows: { key: string; label: string }[];
  columns: { key: string; label: string }[];
  /** `[rowKey][columnKey]`; a missing entry renders as an empty cell. */
  cells: Record<
    string,
    Record<string, ProductDetailsSectionOffersPlTableCell | undefined>
  >;
  /** Absent when the current selection falls into no cell. */
  selected?: { rowKey: string; columnKey: string };
  onCellSelect: (rowKey: string, columnKey: string) => void;
  onColumnSelect: (columnKey: string) => void;
  onOpenDetails: () => void;
};

// Amount-by-tenure reward matrix with a sticky loan-amount column. Mobile:
// fixed-width columns that scroll; md+: the columns fill the width, and their
// labels stay on one line, so the matrix scrolls when they don't fit (a tablet
// with the Under-12 column). A cell is two-step: the first click selects it, a
// click on the selected cell opens the offer details.
export const ProductDetailsSectionOffersPlTable: React.FC<
  ProductDetailsSectionOffersPlTableProps
> = ({
  rowHeaderLabel,
  rows,
  columns,
  cells,
  selected,
  onCellSelect,
  onColumnSelect,
  onOpenDetails,
}) => {
  const context = useCmsClientContext();
  const text = getClientTranslations(TRANSLATIONS, context);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Measured after mount so SSR and the first client render agree; re-measured
  // on scroll and whenever the container or the table itself resizes.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const update = () =>
      setCanScrollRight(
        container.scrollLeft + container.clientWidth <
          container.scrollWidth - 1,
      );
    update();

    container.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(container);
    if (container.firstElementChild) {
      observer.observe(container.firstElementChild);
    }

    return () => {
      container.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  const scrollRight = () => {
    const container = scrollRef.current;
    container?.scrollBy({
      left: container.clientWidth / 2,
      behavior: 'smooth',
    });
  };

  // Gaps between cells live in the cell padding, so the sticky column's
  // background also covers the gap next to it while the columns scroll.
  const gapClass = (isLastRow: boolean, isLastColumn: boolean) =>
    classNames('h-px p-0', {
      'pb-spacing/gap/soft-8px': !isLastRow,
      'pr-spacing/gap/soft-8px': !isLastColumn,
    });
  const stickyClass = classNames('sticky left-0 z-10 bg-fill/card');
  const boxClass = classNames(
    'flex size-full rounded-radius/card-inner px-form/inner-element-padding',
  );
  const headerBoxClass = classNames(boxClass, 'py-spacing/margin/y');
  const bodyBoxClass = classNames(boxClass, 'py-button/padding-regular-x');

  return (
    <div className="relative isolate">
      <div ref={scrollRef} className="overflow-x-auto no-scrollbar">
        <table className="w-full table-fixed border-separate border-spacing-0 md:table-auto md:whitespace-nowrap">
          <colgroup>
            <col className="w-29 md:w-47" />
            {columns.map((column) => (
              <col key={column.key} className="w-24 md:w-auto" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                scope="col"
                className={classNames(
                  stickyClass,
                  gapClass(false, columns.length === 0),
                )}
              >
                <div
                  className={classNames(
                    headerBoxClass,
                    'items-center justify-center text-center mhg-alpha-typography-table-cell-strong text-text/primary md:border md:border-borders/default',
                  )}
                >
                  {rowHeaderLabel}
                </div>
              </th>
              {columns.map((column, columnIndex) => {
                const isSelected = selected?.columnKey === column.key;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={gapClass(
                      false,
                      columnIndex === columns.length - 1,
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => onColumnSelect(column.key)}
                      className={classNames(
                        headerBoxClass,
                        'items-center justify-center mhg-alpha-typography-table-cell-strong',
                        isSelected
                          ? 'bg-fill/primary text-text/on-dark'
                          : 'bg-fill/base text-text/primary',
                      )}
                    >
                      {column.label}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isLastRow = rowIndex === rows.length - 1;
              const isSelectedRow = selected?.rowKey === row.key;
              return (
                <tr key={row.key}>
                  <th
                    scope="row"
                    className={classNames(
                      stickyClass,
                      gapClass(isLastRow, columns.length === 0),
                    )}
                  >
                    <div
                      className={classNames(
                        bodyBoxClass,
                        'items-center text-left mhg-alpha-typography-table-cell-norm',
                        isSelectedRow
                          ? 'bg-fill/primary text-text/on-dark'
                          : 'bg-fill/base text-text/primary',
                      )}
                    >
                      {row.label}
                    </div>
                  </th>
                  {columns.map((column, columnIndex) => {
                    const cell = cells[row.key]?.[column.key];
                    const isSelected =
                      isSelectedRow && selected?.columnKey === column.key;
                    const cellBoxClass = classNames(
                      bodyBoxClass,
                      'flex-col items-center justify-center gap-spacing/padding/soft-4px text-center',
                    );

                    return (
                      <td
                        key={column.key}
                        className={gapClass(
                          isLastRow,
                          columnIndex === columns.length - 1,
                        )}
                      >
                        {!cell || cell.kind === 'empty' ? (
                          <div
                            className={classNames(
                              cellBoxClass,
                              'bg-fill/card text-text/secondary-lighter mhg-alpha-typography-table-cell-strong',
                            )}
                          >
                            -
                          </div>
                        ) : (
                          <button
                            type="button"
                            aria-pressed={isSelected}
                            aria-haspopup={isSelected ? 'dialog' : undefined}
                            onClick={
                              isSelected
                                ? onOpenDetails
                                : () => onCellSelect(row.key, column.key)
                            }
                            className={classNames(
                              cellBoxClass,
                              isSelected
                                ? 'bg-fill/primary text-text/on-dark'
                                : 'bg-fill/card text-text/primary',
                            )}
                          >
                            {cell.kind === 'value' && (
                              <span
                                className={classNames(
                                  'text-font-size/caption leading-line-height/table-cell',
                                  isSelected
                                    ? 'text-text/on-dark'
                                    : 'text-text/secondary-lighter',
                                )}
                              >
                                {tr('rewards_up_to', text)}
                              </span>
                            )}
                            <span className="flex items-center gap-spacing/padding/soft-4px">
                              <span className="mhg-alpha-typography-table-cell-strong underline">
                                {cell.kind === 'value'
                                  ? cell.value
                                  : tr('see_details', text)}
                              </span>
                              {isSelected && (
                                <MaterialIcon
                                  icon="arrow_right_alt"
                                  px={18}
                                  className="motion-safe:animate-nudge-x"
                                />
                              )}
                            </span>
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll to see more tenures"
          onClick={scrollRight}
          className="absolute right-0 top-1/2 z-20 flex size-[30px] -translate-y-1/2 items-center justify-center rounded-radius/card bg-fill/card text-text/secondary-lighter shadow-md"
        >
          <MaterialIcon icon="arrow_right_alt" px={20} />
        </button>
      )}
    </div>
  );
};
